import {HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpRequest, HttpResponse, HttpUrlEncodingCodec} from "@angular/common/http";
import {Injectable} from "@angular/core";

import {ArrayBufferReader, CborDecoder} from "framework/services/cbor";
import {shouldStoreReturnUrl} from "framework/services/return-context.helper";
import {currentComponentId} from "framework/services/trace-headers-interceptor/trace-headers";
import {Lookup} from "framework/services/utils.service";

@Injectable()
export class ApiClientConfiguration
{
    static readonly SESSION_RETURN_KEY = "sessionTimeoutReturnContext";

    private m_isAuthenticatedFn: () => boolean = () => false;
    set isAuthenticatedFn(fn: () => boolean)
    {
        this.m_isAuthenticatedFn = fn || (() => false);
    }

    constructor(public readonly apiHost: string,
                public authToken: string,
                public authUser: string,
                public readonly fileServerUrl: string)
    {}

    public isNotAuthenticatedError(code: string): boolean
    {
        return code === ApiClientErrorCode.Authentication;
    }

    public handleNotAuthenticatedError()
    {
        if (this.m_isAuthenticatedFn()) this.redirectToLogin();
    }

    public errorHandler(error: ErrorResult)
    {
        if (error.validationErrors)
        {
            for (let validationError of error.validationErrors.entries)
            {
                console.error(`APP_ERROR: '${validationError.field}' : ${validationError.reason}`);
            }
        }
        else
        {
            console.error(`APP_ERROR: ${error.code} ${error.message}`);

            if (this.isNotAuthenticatedError(error.code))
            {
                this.handleNotAuthenticatedError();
            }
        }
    }

    public redirectToLogin(url?: string)
    {
        try
        {
            const returnUrl = url || (window.location.pathname + window.location.search);
            if (shouldStoreReturnUrl(returnUrl))
            {
                sessionStorage.setItem(
                    ApiClientConfiguration.SESSION_RETURN_KEY,
                    JSON.stringify({
                                       returnUrl,
                                       userId: localStorage.getItem("NAME") ?? ""
                                   })
                );
            }
        }
        catch (_)
        {
        }
        window.location.pathname = "/site/logout";
    }
}

class CustomHttpUrlEncodingCodec extends HttpUrlEncodingCodec
{
    encodeKey(k: string): string
    {
        k = super.encodeKey(k);
        return k.replace(/\+/gi, "%2B");
    }

    encodeValue(v: string): string
    {
        v = super.encodeValue(v);
        return v.replace(/\+/gi, "%2B");
    }
}

export enum ApiClientErrorCode
{
    Authentication = "AUTHENTICATION_ERROR",
    Conflict       = "CONFLICT_ERROR",
    Http           = "HTTP_ERROR",
    Permission     = "PERMISSION_ERROR",
    Unexpected     = "UNEXPECTED_ERROR"
}

@Injectable()
export class ApiClient
{
    public static disableCBOR: boolean;
    public static readonly IdentifierHeaderName = "X-Request-ID";
    public static readonly ComponentHeaderName  = "X-Component-ID";

    private readonly m_formEncoder = new CustomHttpUrlEncodingCodec();

    constructor(private http: HttpClient,
                public configuration: ApiClientConfiguration)
    {
        console.info(`Creating api client. Domain: ${this.configuration.apiHost}...`);
    }

    async callWithOptions<T>(path: string,
                             requestOptions: ApiRequest): Promise<T>
    {
        let responseType: "arraybuffer" | "blob" | "json" | "text" = "text";

        // Bypass service workers.
        requestOptions.setHeader("ngsw-bypass", "true");

        if (this.configuration.authToken) requestOptions.setHeader("X-Auth-Token", this.configuration.authToken);
        if (this.configuration.authUser) requestOptions.setHeader("X-Auth-User", this.configuration.authUser);
        requestOptions.setHeader(ApiClient.IdentifierHeaderName, crypto.randomUUID());

        const componentId = currentComponentId();
        if (componentId) requestOptions.setHeader(ApiClient.ComponentHeaderName, componentId);

        let body = this.setConsumeContentType(requestOptions);

        if (requestOptions.produces)
        {
            if (!ApiClient.disableCBOR && requestOptions.produces.indexOf("application/cbor") !== -1)
            {
                requestOptions.setHeader("Accept", "application/cbor");
                responseType = "arraybuffer";
            }
            else if (requestOptions.produces.indexOf("application/json") !== -1)
            {
                requestOptions.setHeader("Accept", "application/json");
                responseType = "json";
            }
            else if (requestOptions.produces.indexOf("application/octet-stream") !== -1)
            {
                requestOptions.setHeader("Accept", "application/octet-stream");
                responseType = "blob";
            }
        }

        // -------------------------

        let request = new HttpRequest<any>(requestOptions.method, path, body, {
            headers        : requestOptions.headers,
            params         : new HttpParams({fromObject: requestOptions.queryParams}),
            responseType   : responseType,
            withCredentials: true // enable credentials (allows app to keep session cookies)
        });

        let response: HttpEvent<T>;

        try
        {
            // create request
            response = await this.http.request<T>(request)
                                 .toPromise();
        }
        catch (error)
        {
            let context = `${requestOptions.method} ${path}`;

            switch (error.status)
            {
                case 412: // handle "application" errors identify during handleHttpSuccess
                    if (error.error)
                    {
                        let result: ErrorResult;

                        if (typeof error.error == "string")
                        {
                            result = <ErrorResult>JSON.parse(error.error);
                        }
                        else
                        {
                            result = <ErrorResult>{validationErrors: error.error};
                        }

                        this.configuration.errorHandler(result);

                        throw result;
                    }
                    break;

                case 401:
                    console.error(`HTTP_ERROR: Status code ${error.status} on ${context}`);

                    let errorMessage = "You must be logged in to use the requested feature";
                    if (error?.error?.message)
                    {
                        errorMessage = error.error?.message;
                    }

                    this.configuration.handleNotAuthenticatedError();

                    throw {
                        code   : ApiClientErrorCode.Authentication,
                        message: errorMessage,
                        context: context
                    };

                case 403: // else handle the http errors
                    console.error(`HTTP_ERROR: Status code ${error.status} on ${context}`);

                    throw {
                        code   : ApiClientErrorCode.Permission,
                        message: error.error?.message || "You don't have access to the requested feature",
                        context: context
                    };
                case 409:
                    console.error(`HTTP_ERROR: Status code ${error.status} on ${context}`);

                    throw {
                        code   : ApiClientErrorCode.Conflict,
                        message: error.error?.message || "There is a conflict with the requested feature",
                        context: context
                    };
            }

            if (error.url.includes("/site/login"))
            {
                const sessionTimeoutError = {
                    code   : ApiClientErrorCode.Http,
                    message: "Session Timeout, Please login to continue",
                    context: context
                };

                setTimeout(() =>
                           {
                               window.location.href = "/site/login";
                           }, 500);

                throw sessionTimeoutError;
            }


            // else handle the http errors
            if (error.status)
            {
                console.error(`HTTP_ERROR: Status code ${error.status} on ${context}`);

                throw {
                    code   : ApiClientErrorCode.Http,
                    message: error.error?.message || `An unexpected error occurred`,
                    context: context
                };
            }

            // handle any other errors
            console.error(`ERROR: A non-HTTP error occurred: ${error?.message ?? error}`);

            throw {
                code   : ApiClientErrorCode.Unexpected,
                message: `An unexpected system error occurred for ${context}`,
                context: context
            };
        }

        if (response instanceof HttpResponse)
        {
            if (response.status === 204)
            {
                return undefined;
            }
            else
            {
                let contentType = response.headers.get("content-type");
                contentType     = contentType.split(";")[0].trim();
                switch (contentType)
                {
                    case "application/cbor":
                        let reader      = new ArrayBufferReader(<ArrayBuffer><any>response.body);
                        let cborDecoder = new CborDecoder(reader);
                        return <T>cborDecoder.decode();

                    case "application/json":
                        return <T>response.body;

                    case "application/octet-stream":
                        return <T>response.body;

                    case "text/plain":
                        return <T>response.body;

                    case "text/html":
                        return <T>response.body;

                    default:
                        throw TypeError(`Unsupported response content type (${contentType}).`);
                }
            }
        }

        return undefined;
    }

    private setConsumeContentType(requestOptions: ApiRequest): any
    {
        if (requestOptions.hasBodyParam)
        {
            if (!requestOptions.consumes)
            {
                return this.setConsumeContentType_JSON(requestOptions);
            }

            for (let consume of requestOptions.consumes)
            {
                switch (consume)
                {
                    case "application/json":
                        return this.setConsumeContentType_JSON(requestOptions);

                    case "application/octet-stream":
                        return this.setConsumeContentType_OctetStream(requestOptions);
                }
            }
        }
        else if (requestOptions.hasFormParams)
        {
            requestOptions.setHeader("Content-Type", "application/x-www-form-urlencoded");

            return new HttpParams({
                                      encoder   : this.m_formEncoder,
                                      fromObject: requestOptions.formParams
                                  }).toString();
        }

        if (requestOptions.consumes?.length > 0)
        {
            requestOptions.setHeader("Content-Type", requestOptions.consumes[0]);
        }

        return undefined;
    }

    private setConsumeContentType_JSON(requestOptions: ApiRequest): any
    {
        requestOptions.setHeader("Content-Type", "application/json");
        return requestOptions.body == null ? "" : JSON.stringify(requestOptions.body); // https://github.com/angular/angular/issues/10612
    }

    private setConsumeContentType_OctetStream(requestOptions: ApiRequest): any
    {
        requestOptions.setHeader("Content-Type", "application/octet-stream");
        return requestOptions.body;
    }
}

export class ApiRequest
{
    method: string = "GET";

    formParams: Lookup<string | string[]>;
    queryParams: Lookup<string | string[]>;

    headers: HttpHeaders = new HttpHeaders();

    body: any;

    consumes: string[];
    produces: string[];

    hasFormParams: boolean;
    hasBodyParam: boolean;
    // responseType: "arraybuffer" | "blob" | "json" | "text" = "json";

    //--//

    public setConsume(type: string)
    {
        if (!this.consumes) this.consumes = [];

        this.consumes.push(type);
    }

    public setProduce(type: string)
    {
        if (!this.produces) this.produces = [];

        this.produces.push(type);
    }

    public setHeader(name: string,
                     value: string | string[]): void
    {
        this.headers = this.headers.set(name, value);
    }

    public setFormParam(name: string,
                        value: string): void
    {
        if (!this.formParams) this.formParams = {};

        ApiRequest.setParam(this.formParams, name, value);
    }

    public setQueryParam(name: string,
                         value: string): void
    {
        if (!this.queryParams) this.queryParams = {};

        ApiRequest.setParam(this.queryParams, name, value);
    }

    private static setParam(params: Lookup<string | string[]>,
                            name: string,
                            value: string): void
    {
        if (value === null || value === undefined) return;

        let oldValue = params[name];
        if (oldValue)
        {
            if (Array.isArray(oldValue))
            {
                let array = <string[]>oldValue;
                array.push(value);
            }
            else
            {
                let array = [<string>oldValue];
                array.push(value);
                params[name] = array;
            }
        }
        else
        {
            params[name] = value;
        }
    }
}

export interface ErrorResult
{
    code: string;

    message: string;

    exceptionTrace: string;

    validationErrors: ValidationErrors;
}

export interface ValidationErrors
{
    entries: ValidationError[];
}

export interface ValidationError
{
    field: string;

    reason: string;
}
